import { Crop, FileText, Upload, X } from 'lucide-react';
import { useCallback } from 'react';
import { FileInput } from '../../components/ui/file-input';
import { ProductImage } from '../../components/ui/product-image';
import { useFilePrepare } from '../../hooks/useFilePrepare';
import { IMAGE_ACCEPT_ATTR, IMAGE_ACCEPT_HINT, StagedImage } from '../../lib/product-image-upload';

/** SVGs are vectors — the raster crop pipeline can't crop them, so crop is disabled. */
const isSvgFile = (file: File) => file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);

interface ProductImageStagerProps {
	images: StagedImage[];
	/** Raw files dropped or picked (already HEIC-converted by FileInput). */
	onAddFiles: (files: File[]) => void;
	onRemove: (index: number) => void;
	/** Opens the (inline) crop editor for a staged image. */
	onCropImage: (index: number) => void;
	/** Optional node rendered in the pending-files header (e.g. an "Upload all" button). */
	headerAction?: React.ReactNode;
	/** Disables remove controls while an upload is in flight. */
	disabled?: boolean;
}

/**
 * Dropzone + list of files staged for upload. Purely presentational: the
 * parent owns the staged-image state and decides when to upload them.
 */
export function ProductImageStager({
	images,
	onAddFiles,
	onRemove,
	onCropImage,
	headerAction,
	disabled,
}: ProductImageStagerProps) {
	const { prepare, converting: dropConverting } = useFilePrepare();

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	const handleDrop = useCallback(
		async (e: React.DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			const dropped = Array.from(e.dataTransfer.files);
			if (!dropped.length) return;
			const valid = await prepare(dropped, IMAGE_ACCEPT_ATTR);
			if (valid.length) onAddFiles(valid);
		},
		[onAddFiles, prepare]
	);

	return (
		<div className="flex flex-col gap-4">
			{/* Drop zone */}
			<FileInput onFiles={onAddFiles} accept={IMAGE_ACCEPT_ATTR} multiple>
				{({ open, converting }) => (
					<div
						onDragOver={handleDragOver}
						onDrop={handleDrop}
						onClick={open}
						className="flex cursor-pointer flex-col items-center gap-2.5 rounded-[var(--r-3)] border border-dashed border-line-2 px-4 py-7 text-center transition-colors hover:border-line-3 hover:bg-surface-hover"
					>
						<div className="flex h-10 w-10 items-center justify-center rounded-full border border-line-1 bg-surface-fill">
							<Upload size={16} className="text-text-3" />
						</div>
						<div>
							<div className="text-base font-medium text-text-2">
								{converting || dropConverting
									? 'Converting…'
									: 'Drop files or click to browse'}
							</div>
							<div className="mt-0.5 text-xs text-text-4">{IMAGE_ACCEPT_HINT}</div>
						</div>
					</div>
				)}
			</FileInput>

			{/* Pending files */}
			{images.length > 0 && (
				<div>
					<div className="mb-2 flex items-center justify-between">
						<div className="text-xs font-medium uppercase tracking-[0.06em] text-text-3">
							New files ({images.length})
						</div>
						{headerAction}
					</div>
					<div className="flex flex-col gap-1.5">
						{images.map((img, i) => (
							<div
								key={i}
								className="flex items-center gap-3 rounded-[var(--r-3)] border border-line-1 bg-surface-fill/60 px-3 py-2"
							>
								{img.preview ? (
									<ProductImage
										image={{ url: img.preview, crop: img.crop }}
										className="h-9 w-9 shrink-0 rounded-[var(--r-2)] border border-line-1"
									/>
								) : (
									<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill">
										<FileText size={14} className="text-text-4" />
									</div>
								)}
								<div className="min-w-0 flex-1">
									<div className="truncate text-sm font-medium text-text-1">
										{img.file.name}
									</div>
									<div className="text-xs text-text-4">
										{(img.file.size / 1024).toFixed(0)} KB
										{img.crop && ' · cropped'}
									</div>
								</div>
								{img.preview && (
									<button
										type="button"
										disabled={disabled || isSvgFile(img.file)}
										onClick={() => onCropImage(i)}
										title={
											isSvgFile(img.file)
												? "SVG images can't be cropped"
												: 'Crop'
										}
										className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-text-4 transition-colors hover:text-text-1 disabled:cursor-default disabled:opacity-50"
									>
										<Crop size={12} />
									</button>
								)}
								<button
									type="button"
									disabled={disabled}
									onClick={() => onRemove(i)}
									className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-text-4 transition-colors hover:text-text-1 disabled:cursor-default disabled:opacity-50"
								>
									<X size={12} />
								</button>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
